import { Card, Flex, Table } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import APIList from '../../../../http/ApiList';
import { useSelectorSize } from '../../../../hooks/useWindowSize';
import { COMPARE, FIELD_CATEGORY, FIELD_IMPORTANT, PAGE_ID, SORT_SHOW } from '../../../../enum/common.enum';
import { kpiValue, toCamelCase } from '../../../../utils/farecast-util';
import { Color } from 'antd/es/color-picker';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import { groupBy, isNil, round, sortBy } from 'lodash';
import { getS3Object } from '@meteor/frontend-core';
import dayjs from 'dayjs';
import { NAME_PATH, useTralitSetting } from '../../../../hooks/useTralitSetting';
import { useLabel } from '../../../../hooks/useLabel';

const CompareTable: React.FC<any> = (props) => {
  const { property } = props;
  const { color, event$, tralitSettings } = useContext(AppContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const label = useLabel();
  // 完整数据
  const [dataProvider, setDataProvider] = useState({ fields: [], compareData: [] });
  // Loading
  const [tableLoading, setTableLoading] = useState(true);
  // 比较对象
  const [compare, setCompare] = useState<any>('snapshot');
  // 比较KPI
  const [compareKpiKey, setCompareKpiKey] = useState();
  // 比较KPI Fields
  const fieldKpi = useMemo(() => {
    const { fields } = dataProvider;
    if (compareKpiKey) {
      return fields.find((field) => compareKpiKey === field.fieldKey) || {};
    }
    const findCompareKPI = fields.find((field) => field.category === FIELD_CATEGORY.KPI_DATA && field.isCompare);
    return findCompareKPI || {};
  }, [dataProvider, compareKpiKey]);
  // Target Label
  const [tarLabel, setTarLabel] = useState();
  // Current Label
  const [curLabel, setCurLabel] = useState();
  // 获取Table滚动条高度
  const { selectorHeight4Table } = useSelectorSize({
    selector: '.tralit-table-tbody',
    watcherSelector: ['.tralit-table-header'],
  });
  const [filteredInfo, setFilteredInfo] = useState<any[]>([]);
  const [sortedOrder, setSortedOrder] = useState<any>({});

  const [sortShow, setSortShow] = useState(SORT_SHOW.DEVIATION);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  useEffect(() => {
    const params = Object.fromEntries(searchParams);
    setSortShow(SORT_SHOW.TREND);
    if (params.actionCompare === COMPARE.PLANNED_RATIO) {
      setSortedOrder({ current: 'descend' });
      setFilteredInfo([
        {
          fieldKey: 'order_category',
          value: 'C. 未受注案件',
        },
      ]);
    }
  }, [searchParams]);

  /**
   * 调用API获取数据
   */
  const getTableData = (req) => {
    setTableLoading(true);
    setCompareKpiKey(req.metric);
    const {
      fiscalYear,
      evaluationPeriod,
      dimension,
      currentSnapshot,
      targetSnapshot,
      currentPeriod = [],
      targetPeriod = [],
      aggregationPeriod,
      compare,
    } = req;
    const [startDate, endDate] = aggregationPeriod || [];
    const tarLabelMap = {
      snapshot: (
        <Flex vertical>
          <span>断面合計</span>({dayjs(targetSnapshot).format('MM-DD')})
        </Flex>
      ),
      period: `期間合計${targetPeriod.length > 0 ? `(${targetPeriod[0].format('YY-MM')} ~ ${targetPeriod[1].format('YY-MM')})` : ''}`,
      plan: label.plan,
      prev_year: '前年',
      prev_month: '前月',
    };
    const curLabelMap = {
      snapshot: (
        <Flex vertical>
          <span>断面合計</span>({dayjs(currentSnapshot).format('MM-DD')})
        </Flex>
      ),
      period: `期間合計${currentPeriod.length > 0 ? `(${currentPeriod[0].format('YY-MM')} ~ ${currentPeriod[1].format('YY-MM')})` : ''}`,
      plan: fieldKpi.label,
      prev_year: '今年',
      prev_month: '今月',
    };
    setTarLabel(tarLabelMap[compare]);
    setCurLabel(curLabelMap[compare]);
    const paramMap = {
      snapshot: {
        snapshot: [currentSnapshot, targetSnapshot],
        compare: 'snapshot',
        reportType: 'monthly',
      },
      period: {
        snapshot: [currentSnapshot],
        compare: 'period',
        reportType: 'monthly',
        timeFilter: [
          {
            startDate: currentPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: currentPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
          {
            startDate: targetPeriod[0]?.startOf('month').format('YYYY-MM-DD'),
            endDate: targetPeriod[1]?.endOf('month').format('YYYY-MM-DD'),
          },
        ],
      },
      plan: {
        snapshot: [currentSnapshot],
        compare: 'plan',
        reportType: 'monthly',
      },
      prev_year: {
        snapshot: [currentSnapshot],
        compare: 'prev_year',
        reportType: 'monthly',
      },
      prev_month: {
        snapshot: [currentSnapshot],
        compare: 'prev_month',
        reportType: 'monthly',
      },
    };
    const params = {
      websocketRoute: 'WebsocketOpenaiEvent',
      client_account: tralitSettings.account,
      startDate: startDate?.format('YYYY-MM-DD'),
      endDate: endDate?.format('YYYY-MM-DD'),
      fiscalYear,
      evaluationPeriod,
      reportType: dimension,
      ...paramMap[compare],
    };
    APIList.getCompareTableData()
      .post(params)
      .then(async (res) => {
        const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME, res.bucketKey);
        setDataProvider({
          ...response,
          fields: response.fields.filter((field) => !field.classification || field.classification.includes('fluctuation')),
        });
        setFilteredInfo([]);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  /**
   * 事件监听：PAGE_INIT， FILTER_ITEM_CHANGED
   */
  event$.useSubscription((globalEvent) => {
    if (!globalEvent || globalEvent.pageId !== PAGE_ID.COMPARE) {
      return;
    }
    if (globalEvent.i && property.i !== globalEvent.i) {
      return;
    }
    if (globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT) {
      setCompare(globalEvent.data.compare);
      getTableData(globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.FILTER_ITEM_CHANGED) {
      setCompare(globalEvent.data.compare);
      getTableData(globalEvent.data);
    }
    if (globalEvent.eventType === GLOBAL_EVENT.CHART_ITEM_CLICK) {
      setFilteredInfo((filteredInfo) => {
        const field = filteredInfo.find((field) => field.fieldKey === globalEvent.data.fieldKey && field.value);
        if (field) {
          return filteredInfo.filter((field) => field.fieldKey !== globalEvent.data.fieldKey);
        } else {
          return [
            ...filteredInfo,
            {
              fieldKey: globalEvent.data.fieldKey,
              value: globalEvent.data.value,
            },
          ];
        }
      });
      const sortMap = {
        超過分: 'descend',
        未達分: 'ascend',
      };
      setSortedOrder({ diff: sortMap[globalEvent.data.seriesName] || null });
    }
    if (globalEvent.eventType === GLOBAL_EVENT.DATA_SORT) {
      if (globalEvent.data.sortKey === 'diff') {
        setSortedOrder({ diff: globalEvent.data.value || 'ascend' });
      } else {
        setSortedOrder(
          globalEvent.data.sortKey.split('&').reduce((acc, cur) => {
            acc[cur] = 'ascend';
            return acc;
          }, {})
        );
      }
    }
    if (globalEvent.eventType === GLOBAL_EVENT.SORT_CHANGE) {
      setSortShow(globalEvent.value);
    }
  });

  // Table Columns
  const columns: any[] = useMemo(() => {
    const render = (text) => kpiValue(text, fieldKpi.unitValue);
    const renderDiff = (text) => {
      if (text >= 0) {
        return <span style={{ color: color.successColor }}>{kpiValue(text, fieldKpi.unitValue, true, 4)}</span>;
      } else {
        return <span style={{ color: color.errorColor }}>{kpiValue(text, fieldKpi.unitValue, true, 4)}</span>;
      }
    };
    const renderRatio = (text) => {
      if (isNil(text)) {
        return '-';
      }
      if (text >= 0) {
        return <span style={{ color: color.successColor }}>{`+${round(text * 100)}%`}</span>;
      } else {
        return <span style={{ color: color.errorColor }}>{`-${Math.abs(round(text * 100))}%`}</span>;
      }
    };

    const { fields } = dataProvider;

    const getFilteredValue = (field) => {
      if (filteredInfo.length > 0) {
        const info = filteredInfo.find((info) => info.fieldKey.includes(field.fieldKey));
        if (info) {
          const fieldKeys = info.fieldKey.split('&');
          if (typeof info?.value === 'string') {
            const values = info?.value.split('&');
            return fieldKeys.reduce((acc, fieldKey, index) => {
              if (fieldKey === field.fieldKey) {
                const value = values[index];
                acc.push(value);
              }
              return acc;
            }, []);
          } else if (info?.fieldKey === field.fieldKey) {
            return info?.value;
          }
        }
      }
    };

    const dynamicColumns = sortBy(fields, 'priority')
      .filter((field) => field.category === 'drill_down')
      .map((field) => ({
        key: field.fieldKey,
        dataIndex: toCamelCase(field.fieldKey),
        title: field.label,
        width: field.width || 100,
        sorter: (a, b) => {
          return a[toCamelCase(field.fieldKey)].localeCompare(b[toCamelCase(field.fieldKey)]);
        },
        sortOrder: sortedOrder[field.fieldKey],
        filterSearch: true,
        filteredValue: getFilteredValue(field),
        filters: Object.keys(groupBy(dataProvider.compareData, toCamelCase(field.fieldKey)))
          .sort()
          .map((item) => ({ text: item, value: item })),
        onFilter: (value, record) => record[toCamelCase(field.fieldKey)]?.startsWith(value),
        fixed: !field.classification ? 'left' : false,
      }));

    const staticColumns = [
      {
        key: 'diff',
        dataIndex: 'diff',
        title: `差分`,
        width: 100,
        className: 'text-right',
        render: renderDiff,
        sorter: (a, b) => {
          if (sortShow === SORT_SHOW.DEVIATION) {
            return Math.abs(a.diff) - Math.abs(b.diff);
          }
          return a.diff - b.diff;
        },
        sortOrder: sortedOrder.diff,
      },
      {
        key: 'ratio',
        dataIndex: 'ratio',
        title: `比率`,
        width: 100,
        className: 'text-right',
        render: renderRatio,
        sorter: (a, b) => a.ratio - b.ratio,
        sortOrder: sortedOrder.ratio,
      },
      {
        key: 'target',
        dataIndex: 'target',
        title: tarLabel,
        width: 120,
        className: 'text-right',
        render,
        sorter: (a, b) => a.target - b.target,
        sortOrder: sortedOrder.target,
      },
      {
        key: 'current',
        dataIndex: 'current',
        title: curLabel,
        width: 120,
        className: 'text-right',
        render,
        sorter: (a, b) => {
          return a.current - b.current;
        },
        sortOrder: sortedOrder.current,
      },
    ];
    return [...dynamicColumns, ...staticColumns];
  }, [dataProvider, fieldKpi, tarLabel, curLabel, filteredInfo, sortedOrder, sortShow]);

  useEffect(() => {
    setTableLoading(true);
  }, [tralitSettings.account]);

  return (
    <>
      <Card
        styles={{ body: { padding: 0 }, header: { color: '#555', fontWeight: 'bold', fontSize: fontSize.tableTitle, padding: '0 12px' } }}
        classNames={{ header: 'list-card-title' }}
        title={`${fieldKpi.label || ''}変動一覧`}
        extra={`単位: ${fieldKpi.unitName || '円'}`}
      >
        <Table
          virtual
          size="small"
          className="small-header"
          bordered
          rowHoverable={false}
          rowKey={(record) => dataProvider.fields.map((field) => record[toCamelCase(field.fieldKey)]).join()}
          columns={columns}
          dataSource={dataProvider.compareData}
          pagination={false}
          scroll={{
            x: 'max-content',
            y: selectorHeight4Table,
          }}
          loading={tableLoading}
          onChange={(pagination, filters, sorter: any) => {
            setFilteredInfo((pref) =>
              Object.keys(filters).reduce((acc: any, key) => {
                if (filters[key]) {
                  acc.push({
                    fieldKey: key,
                    value: filters[key],
                  });
                }
                return acc;
              }, [])
            );
            setSortedOrder({
              [sorter.columnKey]: sorter.order,
            });
          }}
        />
      </Card>
    </>
  );
};

export default CompareTable;
